import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FiSearch, FiChevronDown, FiDownload, FiAlertTriangle, FiCamera, FiX, FiFilter, FiTrash2 } from "react-icons/fi";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { getAdminResults, getAdminReport, getAdminViolations, getSnapshotBlob, adminReportViolation, loadQuestionFixtures, getOrgCategoryBreakdown, getSessionCategoryBreakdown, getCategorySessions, deleteAdminSession, deleteUserHistory } from "../api/client";
import { useAuth } from "../context/AuthContext";
import Pagination from "../components/Pagination";

const CATEGORY_PIE_COLORS = [
  "#6366f1", "#22c55e", "#eab308", "#ef4444", "#06b6d4",
  "#a855f7", "#f97316", "#14b8a6", "#ec4899", "#3b82f6",
];
// sidebar handles navigation + theme

const tierColors = {
  "White Belt": "#d1d5db",
  "Yellow Belt": "#eab308",
  "Green Belt": "#22c55e",
  "Black Belt": "#1e1e1e",
};

const beltColorMap = {
  white: "#d1d5db",
  yellow: "#eab308",
  green: "#22c55e",
  black: "#1e1e1e",
  red: "#ef4444",
  blue: "#3b82f6",
  orange: "#f97316",
};

export default function Admin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "completed_at",
    direction: "desc",
  });
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [downloading, setDownloading] = useState(null);
  const [violationsModal, setViolationsModal] = useState(null); // { sessionId, data, galleryUrls }
  const [loadingViolations, setLoadingViolations] = useState(null);
  const [galleryIndex, setGalleryIndex] = useState(null);
  const [galleryTab, setGalleryTab] = useState("gallery"); // "gallery" | "timeline"
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(null);
  const [flaggedIds, setFlaggedIds] = useState(new Set()); // snapshot log IDs flagged by analysis
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 15;
  const [deletingId, setDeletingId] = useState(null); // session_id currently being deleted
  const [deletingUserId, setDeletingUserId] = useState(null); // user_id whose full history is being wiped
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [loadQuestionsResult, setLoadQuestionsResult] = useState(null);
  const [orgCategories, setOrgCategories] = useState([]);
  const [showCategoryPanel, setShowCategoryPanel] = useState(true);
  const [sessionCategories, setSessionCategories] = useState({}); // { sessionId: categories[] | 'loading' | 'error' }
  const [categoryDrilldown, setCategoryDrilldown] = useState(null); // { category, sessions, loading, error }

  // Filters
  const [beltFilter, setBeltFilter] = useState("all");
  const [violationFilter, setViolationFilter] = useState("all");
  const [scoreFilter, setScoreFilter] = useState("all");
  const [deptFilter, setDeptFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    // Quick client-side check
    if (user && !user.is_admin && user.role !== "admin") {
      setAccessDenied(true);
      setLoading(false);
      return;
    }

    const fetchResults = async () => {
      try {
        const [res, catRes] = await Promise.all([
          getAdminResults(),
          getOrgCategoryBreakdown().catch(() => ({ data: { categories: [] } })),
        ]);
        setResults(res.data || []);
        setFilteredResults(res.data || []);
        setOrgCategories(catRes.data?.categories || []);
      } catch (err) {
        if (err.response?.status === 403) {
          setAccessDenied(true);
        } else {
          console.error("Failed to fetch admin results:", err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [user]);

  // Compute unique organizations for filter dropdown
  const uniqueDepts = [...new Set(results.map((r) => r.business_unit).filter(Boolean))].sort();

  useEffect(() => {
    let filtered = results.filter((item) => {
      // Search
      const query = searchQuery.toLowerCase();
      if (query && !(
        item.name?.toLowerCase().includes(query) ||
        item.email?.toLowerCase().includes(query)
      )) return false;

      // Belt filter
      if (beltFilter !== "all") {
        if (beltFilter === "none") {
          if (item.belt) return false;
        } else if ((item.belt_color || "").toLowerCase() !== beltFilter) return false;
      }

      // Violation filter
      if (violationFilter === "flagged" && !item.has_violations) return false;
      if (violationFilter === "clean" && item.has_violations) return false;
      if (violationFilter === "major" && (item.major_violations || 0) === 0) return false;
      if (violationFilter === "minor" && (item.major_violations || 0) > 0) return false;
      if (violationFilter === "minor" && (item.minor_violations || 0) === 0) return false;

      // Score filter
      if (scoreFilter !== "all") {
        const pct = item.score_percentage || 0;
        if (scoreFilter === "90+" && pct < 90) return false;
        if (scoreFilter === "70-89" && (pct < 70 || pct >= 90)) return false;
        if (scoreFilter === "50-69" && (pct < 50 || pct >= 70)) return false;
        if (scoreFilter === "<50" && pct >= 50) return false;
      }

      // Organization filter
      if (deptFilter !== "all") {
        if (deptFilter === "none") {
          if (item.business_unit) return false;
        } else if (item.business_unit !== deptFilter) return false;
      }

      return true;
    });

    filtered = filtered.sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      if (typeof aVal === "string") {
        return sortConfig.direction === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
    });

    setFilteredResults(filtered);
    setCurrentPage(1);
  }, [results, searchQuery, sortConfig, beltFilter, violationFilter, scoreFilter, deptFilter]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const openCategoryDrilldown = async (category) => {
    setCategoryDrilldown({ category, sessions: null, loading: true, error: null });
    try {
      const res = await getCategorySessions(category);
      setCategoryDrilldown({
        category,
        sessions: res.data?.sessions || [],
        loading: false,
        error: null,
      });
    } catch (err) {
      setCategoryDrilldown({
        category,
        sessions: [],
        loading: false,
        error: err.response?.data?.detail || "Failed to load category sessions",
      });
    }
  };

  const toggleExpand = (index, sessionId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
      // Lazy-fetch per-session category breakdown the first time the row is opened
      if (sessionId && !sessionCategories[sessionId]) {
        setSessionCategories((prev) => ({ ...prev, [sessionId]: "loading" }));
        getSessionCategoryBreakdown(sessionId)
          .then((res) => {
            setSessionCategories((prev) => ({
              ...prev,
              [sessionId]: res.data?.categories || [],
            }));
          })
          .catch(() => {
            setSessionCategories((prev) => ({ ...prev, [sessionId]: "error" }));
          });
      }
    }
    setExpandedRows(newExpanded);
  };

  const handleDeleteSession = async (item) => {
    const ok = window.confirm(
      `Delete this attempt (session #${item.session_id}) for ${item.name}?\n\nThis removes the attempt, its answers, and proctoring logs. Cannot be undone.`
    );
    if (!ok) return;
    setDeletingId(item.session_id);
    try {
      await deleteAdminSession(item.session_id);
      setResults((prev) => prev.filter((r) => r.session_id !== item.session_id));
    } catch (err) {
      console.error("Failed to delete session:", err);
      alert(err.response?.data?.detail || "Failed to delete session");
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteUserHistory = async (item) => {
    const ok = window.confirm(
      `Delete ALL assessment history for ${item.name} (${item.email})?\n\nThis wipes every attempt plus retake permissions — the user can take the assessment again from scratch. Cannot be undone.`
    );
    if (!ok) return;
    setDeletingUserId(item.user_id);
    try {
      await deleteUserHistory(item.user_id);
      setResults((prev) => prev.filter((r) => r.user_id !== item.user_id));
    } catch (err) {
      console.error("Failed to delete user history:", err);
      alert(err.response?.data?.detail || "Failed to delete user history");
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleDownloadReport = async (item) => {
    setDownloading(item.session_id);
    try {
      const res = await getAdminReport(item.session_id);
      const report = res.data;
      const tierColor = tierColors[report.results.tier] || "#6366f1";

      const htmlContent = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>AIQ Report - ${report.user.name}</title>
  <style>
    body { font-family: 'Inter', Arial, sans-serif; margin: 0; padding: 20px; background: white; color: #333; }
    .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #6366f1; padding-bottom: 20px; }
    .header h1 { margin: 0; color: #6366f1; }
    .header p { color: #666; margin: 4px 0; }
    .score-section { background: #f8f9fa; padding: 20px; border-radius: 8px; margin-bottom: 20px; text-align: center; }
    .score-large { font-size: 48px; font-weight: bold; color: ${tierColor}; }
    .tier-badge { display: inline-block; background: ${tierColor}25; color: ${tierColor}; padding: 10px 20px; border-radius: 24px; margin: 10px 0; font-weight: 600; }
    .stats { display: flex; gap: 30px; justify-content: center; margin: 20px 0; }
    .stat { text-align: center; }
    .stat-value { font-size: 24px; font-weight: bold; color: #6366f1; }
    .stat-label { font-size: 12px; text-transform: uppercase; color: #999; }
    .breakdown-title { font-size: 20px; font-weight: 600; margin: 30px 0 20px; color: #333; }
    .question-item { margin-bottom: 20px; padding-bottom: 20px; border-bottom: 1px solid #eee; }
    .question-number { font-weight: 600; color: #6366f1; }
    .question-meta { display: inline-flex; gap: 12px; margin-left: 12px; font-size: 13px; color: #888; font-weight: 400; }
    .question-time { background: #f0f0f0; padding: 2px 8px; border-radius: 10px; font-size: 12px; color: #555; font-weight: 500; }
    .question-text { font-size: 16px; margin: 10px 0; }
    .correct-answer { color: #10b981; }
    .incorrect-answer { color: #ef4444; }
    .answer-label { font-weight: 600; }
    .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #999; }
    @media print { body { margin: 0; padding: 10px; } }
  </style>
</head>
<body>
  <div class="header">
    <h1>AIQ Assessment Report</h1>
    <p><strong>${report.user.name}</strong> (${report.user.email})</p>
    <p>Completed: ${report.session.completed_at ? new Date(report.session.completed_at).toLocaleString() : "N/A"}</p>
  </div>
  <div class="score-section">
    <div class="score-large">${report.results.score_percentage}%</div>
    <div class="tier-badge">${report.results.tier}</div>
    <div class="stats">
      <div class="stat"><div class="stat-value">${report.results.score}</div><div class="stat-label">Correct</div></div>
      <div class="stat"><div class="stat-value">${report.results.total_questions}</div><div class="stat-label">Total</div></div>
      <div class="stat"><div class="stat-value">${report.results.max_complexity_reached}</div><div class="stat-label">Max Level</div></div>
      <div class="stat"><div class="stat-value">${(() => { const total = report.responses.reduce((sum, r) => sum + (r.time_spent_seconds || 0), 0); return Math.floor(total / 60) + "m " + Math.round(total % 60) + "s"; })()}</div><div class="stat-label">Total Time</div></div>
    </div>
  </div>
  ${(() => {
    const times = report.responses.filter(r => r.time_spent_seconds != null).map(r => r.time_spent_seconds);
    if (times.length === 0) return "";
    const avg = times.reduce((a, b) => a + b, 0) / times.length;
    const fastest = Math.min(...times);
    const slowest = Math.max(...times);
    const fmtTime = (s) => s >= 60 ? Math.floor(s / 60) + "m " + Math.round(s % 60) + "s" : Math.round(s) + "s";
    return '<div style="background: #f0f4ff; padding: 16px 20px; border-radius: 8px; margin: 20px 0; display: flex; gap: 40px; justify-content: center;">'
      + '<div class="stat"><div class="stat-value" style="font-size: 20px;">' + fmtTime(avg) + '</div><div class="stat-label">Avg per Question</div></div>'
      + '<div class="stat"><div class="stat-value" style="font-size: 20px; color: #10b981;">' + fmtTime(fastest) + '</div><div class="stat-label">Fastest</div></div>'
      + '<div class="stat"><div class="stat-value" style="font-size: 20px; color: #ef4444;">' + fmtTime(slowest) + '</div><div class="stat-label">Slowest</div></div>'
      + '</div>';
  })()}
  <div class="breakdown-title">Question Breakdown</div>
  ${report.responses.map((resp, i) => `
    <div class="question-item">
      <div class="question-number">Question ${i + 1} - Level ${resp.complexity}${resp.category ? ` - ${resp.category}` : ""}
        <span class="question-meta">
          ${resp.time_spent_seconds != null ? `<span class="question-time">${resp.time_spent_seconds >= 60 ? Math.floor(resp.time_spent_seconds / 60) + "m " + Math.round(resp.time_spent_seconds % 60) + "s" : Math.round(resp.time_spent_seconds) + "s"}</span>` : ""}
        </span>
      </div>
      <div class="question-text">${resp.question_text}</div>
      <div><span class="answer-label">Answer: </span><span class="${resp.is_correct ? "correct-answer" : "incorrect-answer"}">${resp.selected_option}</span></div>
      ${!resp.is_correct ? `<div><span class="answer-label">Correct: </span><span class="correct-answer">${resp.correct_option}</span></div>` : ""}
      ${resp.explanation ? `<p style="color: #666; font-size: 14px; margin-top: 8px;">${resp.explanation}</p>` : ""}
    </div>
  `).join("")}
  <div class="footer">
    <p>Generated from AIQ Assessment Platform</p>
    <p>Report generated on ${new Date().toLocaleDateString()}</p>
  </div>
</body>
</html>`;

      const blob = new Blob([htmlContent], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `AIQ_Report_${report.user.name.replace(/\s+/g, "_")}_${Date.now()}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download report:", err);
    } finally {
      setDownloading(null);
    }
  };

  const handleViewViolations = async (sessionId) => {
    setLoadingViolations(sessionId);
    try {
      const res = await getAdminViolations(sessionId);
      const data = res.data;

      // Pre-load all snapshot thumbnails as blob URLs
      const snapshotLogs = data.logs.filter((l) => l.has_snapshot);
      const galleryUrls = {};
      await Promise.all(
        snapshotLogs.map(async (log) => {
          try {
            const blob = await getSnapshotBlob(log.id);
            galleryUrls[log.id] = URL.createObjectURL(blob.data);
          } catch {
            galleryUrls[log.id] = null;
          }
        })
      );

      setFlaggedIds(new Set());
      setAnalysisProgress(null);
      setViolationsModal({ sessionId, data, galleryUrls });
      setGalleryTab("gallery");
      setGalleryIndex(null);
    } catch (err) {
      console.error("Failed to fetch violations:", err);
    } finally {
      setLoadingViolations(null);
    }
  };

  const closeViolationsModal = () => {
    // Revoke all blob URLs
    if (violationsModal?.galleryUrls) {
      Object.values(violationsModal.galleryUrls).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    }
    setViolationsModal(null);
    setGalleryIndex(null);
  };

  // ── Show backend auto-analysis results (no more client-side analysis) ──
  const modalSessionId = violationsModal?.sessionId || null;
  useEffect(() => {
    if (!modalSessionId || !violationsModal) return;
    const { data, galleryUrls } = violationsModal;

    // Extract flagged snapshot IDs from backend auto-analysis descriptions
    const autoLogs = data.logs.filter(
      (l) => l.description && l.description.startsWith("[Auto-analysis:")
    );
    const existingFlagged = new Set();
    for (const al of autoLogs) {
      const match = al.description.match(/\[Auto-analysis:(\d+)\]/);
      if (match) existingFlagged.add(parseInt(match[1], 10));
    }
    setFlaggedIds(existingFlagged);
    const snapshotLogs = data.logs.filter((l) => l.has_snapshot && galleryUrls[l.id]);
    setAnalysisProgress({
      done: true,
      flagged: existingFlagged.size,
      total: snapshotLogs.length,
    });
  }, [modalSessionId]);

  // ── Load a script from multiple CDN sources ────────
  const tryLoadScript = async (urls) => {
    for (const url of urls) {
      try {
        await new Promise((res, rej) => {
          const s = document.createElement("script");
          s.src = url;
          const t = setTimeout(() => rej(new Error("timeout")), 12000);
          s.onload = () => { clearTimeout(t); res(); };
          s.onerror = () => { clearTimeout(t); rej(new Error("failed")); };
          document.head.appendChild(s);
        });
        return true;
      } catch { /* next */ }
    }
    return false;
  };

  // ── Analyze all snapshots (face + phone detection) ──
  const analyzeSnapshots = async () => {
    if (!violationsModal) return;
    const { data, galleryUrls } = violationsModal;
    const snapshotLogs = data.logs.filter((l) => l.has_snapshot && galleryUrls[l.id]);
    if (snapshotLogs.length === 0) return;

    setAnalyzing(true);
    setFlaggedIds(new Set());
    setAnalysisProgress(null);
    const newFlagged = new Set();

    try {
      // ── Load face-api.js ──
      let faceReady = false;
      if (!window.faceapi) {
        await tryLoadScript([
          "https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/face-api.min.js",
          "https://unpkg.com/face-api.js@0.22.2/dist/face-api.min.js",
          "https://cdnjs.cloudflare.com/ajax/libs/face-api.js/0.22.2/face-api.min.js",
        ]);
      }
      if (window.faceapi) {
        const MODEL_URLS = [
          "https://justadudewhohacks.github.io/face-api.js/models",
          "https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/weights",
        ];
        for (const url of MODEL_URLS) {
          try {
            await Promise.all([
              window.faceapi.nets.tinyFaceDetector.loadFromUri(url),
              window.faceapi.nets.faceLandmark68TinyNet.loadFromUri(url),
            ]);
            faceReady = true;
            break;
          } catch { /* next */ }
        }
      }

      // ── Load COCO-SSD for phone detection ──
      let cocoModel = null;
      if (!window.tf) {
        await tryLoadScript([
          "https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@4.10.0/dist/tf.min.js",
          "https://unpkg.com/@tensorflow/tfjs@4.10.0/dist/tf.min.js",
        ]);
      }
      if (window.tf && !window.cocoSsd) {
        await tryLoadScript([
          "https://cdn.jsdelivr.net/npm/@tensorflow-models/coco-ssd@2.2.3/dist/coco-ssd.min.js",
          "https://unpkg.com/@tensorflow-models/coco-ssd@2.2.3/dist/coco-ssd.min.js",
        ]);
      }
      if (window.cocoSsd) {
        try {
          cocoModel = await window.cocoSsd.load({ base: "mobilenet_v2" });
        } catch { /* continue without */ }
      }

      if (!faceReady && !cocoModel) {
        alert("Failed to load any detection models");
        setAnalyzing(false);
        return;
      }

      setAnalysisProgress({ current: 0, total: snapshotLogs.length });
      const BANNED = ["cell phone", "remote"];

      // ── Analyze each snapshot ──
      let violationsFound = 0;
      for (let i = 0; i < snapshotLogs.length; i++) {
        const log = snapshotLogs[i];
        setAnalysisProgress({ current: i + 1, total: snapshotLogs.length });

        try {
          const img = new Image();
          img.crossOrigin = "anonymous";
          await new Promise((res, rej) => {
            img.onload = res;
            img.onerror = rej;
            img.src = galleryUrls[log.id];
          });

          // Draw to canvas for reliable tensor conversion
          const cvs = document.createElement("canvas");
          cvs.width = img.naturalWidth || img.width || 640;
          cvs.height = img.naturalHeight || img.height || 480;
          const cctx = cvs.getContext("2d");
          cctx.drawImage(img, 0, 0, cvs.width, cvs.height);

          const violations = [];

          // ── 1. Phone / mobile detection ──
          if (cocoModel) {
            const preds = await cocoModel.detect(cvs);
            for (const pred of preds) {
              if (BANNED.includes(pred.class) && pred.score >= 0.25) {
                violations.push({
                  type: "phone_detected",
                  desc: `Mobile phone detected (${(pred.score * 100).toFixed(0)}% confidence)`,
                });
              }
            }
          }

          // ── 2. Face analysis — ONLY flag: no person, deliberate head turn ──
          if (faceReady) {
            const detections = await window.faceapi
              .detectAllFaces(cvs, new window.faceapi.TinyFaceDetectorOptions({
                inputSize: 320,
                scoreThreshold: 0.3,
              }))
              .withFaceLandmarks(true);

            if (detections.length === 0) {
              // No face at all — person not visible
              violations.push({ type: "no_face", desc: "Person not visible in frame" });
            } else {
              // Find the largest face (the test-taker) — ignore small background faces
              const largest = detections.reduce((a, b) =>
                (b.detection.box.width * b.detection.box.height) >
                (a.detection.box.width * a.detection.box.height) ? b : a
              );
              const box = largest.detection.box;
              const landmarks = largest.landmarks;
              const w = cvs.width || 320;

              // Only check head turn via landmarks on the primary (largest) face
              if (landmarks) {
                const nose = landmarks.getNose();
                const jaw = landmarks.getJawOutline();
                if (nose.length > 0 && jaw.length > 0) {
                  const noseTip = nose[3] || nose[Math.floor(nose.length / 2)];
                  const jawL = jaw[0];
                  const jawR = jaw[jaw.length - 1];
                  const jawW = Math.abs(jawR.x - jawL.x);
                  const jawCx = (jawL.x + jawR.x) / 2;
                  if (jawW > 0) {
                    const offset = (noseTip.x - jawCx) / jawW;
                    if (Math.abs(offset) > 0.32) {
                      const dir = offset > 0 ? "right" : "left";
                      violations.push({ type: "head_turn", desc: `Person looking ${dir} — not facing screen` });
                    }
                  }
                }
              }
            }
          }

          // ── Report all violations for this frame ──
          if (violations.length > 0) {
            newFlagged.add(log.id);
            violationsFound++;
            for (const v of violations) {
              try {
                await adminReportViolation(violationsModal.sessionId, {
                  event_type: v.type,
                  description: `[Auto-analysis:${log.id}] ${v.desc}`,
                });
              } catch { /* best effort */ }
            }
          }
        } catch {
          // skip unanalyzable frames
        }
      }

      setFlaggedIds(newFlagged);
      setAnalysisProgress(
        violationsFound > 0
          ? { done: true, flagged: violationsFound, total: snapshotLogs.length }
          : { done: true, flagged: 0, total: snapshotLogs.length }
      );

      // Small delay so backend finishes persisting all violations
      await new Promise((r) => setTimeout(r, 600));

      // Refresh the modal's violation logs so Violations tab shows new findings
      try {
        const vRes = await getAdminViolations(violationsModal.sessionId);
        setViolationsModal((prev) => prev ? { ...prev, data: vRes.data } : prev);
      } catch { /* best effort */ }

      // Refresh admin results table so has_violations True/False updates
      try {
        const res = await getAdminResults();
        setResults(res.data || []);
      } catch { /* best effort */ }
    } catch (err) {
      console.error("Analysis failed:", err);
      setAnalysisProgress({ error: true });
    } finally {
      setAnalyzing(false);
    }
  };

  const eventTypeLabels = {
    // Major violations (only these 3)
    tab_switch: { label: "Tab Switch", color: "#ef4444", severity: "major" },
    phone_detected: { label: "Mobile Use", color: "#dc2626", severity: "major" },
    no_face: { label: "Not Visible", color: "#ef4444", severity: "major" },
    // Minor violations
    head_turn: { label: "Looking Away", color: "#f97316", severity: "minor" },
    multiple_faces: { label: "Multiple Faces", color: "#f97316", severity: "minor" },
    webcam_denied: { label: "Webcam Denied", color: "#f97316", severity: "minor" },
    tab_refresh: { label: "Tab Refresh", color: "#6b7280", severity: "minor" },
    virtual_camera: { label: "Virtual Camera", color: "#dc2626", severity: "major" },
    external_display: { label: "External Display", color: "#dc2626", severity: "major" },
    window_resize: { label: "Split Screen", color: "#f59e0b", severity: "minor" },
    hand_on_face: { label: "Hand on Face", color: "#6b7280", severity: "minor" },
    // Non-violation
    snapshot: { label: "Snapshot", color: "#6366f1", severity: null },
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-screen">
          <div className="spinner" />
          <p>Loading results...</p>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="admin-page">
        <div className="loading-screen">
          <h2>Access Denied</h2>
          <p style={{ color: "var(--text-secondary)", marginBottom: "20px" }}>
            You do not have permission to view this page.
          </p>
          <button className="btn-primary" onClick={() => navigate("/welcome")}>
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <motion.div
        className="admin-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="admin-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
          <div style={{ minWidth: 0 }}>
            <h1>Quiz Results Dashboard</h1>
            <p>
              Viewing results for {filteredResults.length} record
              {filteredResults.length !== 1 ? "s" : ""}
            </p>
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexShrink: 0 }}>
            <button
              onClick={async () => {
                setLoadingQuestions(true);
                setLoadQuestionsResult(null);
                try {
                  const res = await loadQuestionFixtures();
                  setLoadQuestionsResult(res.data);
                } catch (err) {
                  setLoadQuestionsResult({ error: err.message || "Failed to load questions" });
                } finally {
                  setLoadingQuestions(false);
                }
              }}
              disabled={loadingQuestions}
              style={{
                padding: "8px 16px",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--border-subtle)",
                background: "var(--bg-card)",
                color: "var(--text-primary)",
                cursor: loadingQuestions ? "wait" : "pointer",
                fontSize: "13px",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.15s",
                whiteSpace: "nowrap",
              }}
            >
              {loadingQuestions ? <span className="btn-spinner" /> : null}
              {loadingQuestions ? "Loading..." : "Load Questions"}
            </button>
            {loadQuestionsResult && (
              <span style={{
                fontSize: "12px",
                color: loadQuestionsResult.error ? "#ef4444" : "#22c55e",
                fontWeight: 500,
                whiteSpace: "nowrap",
              }}>
                {loadQuestionsResult.error
                  ? loadQuestionsResult.error
                  : `Added: ${loadQuestionsResult.total_added}, Skipped: ${loadQuestionsResult.total_skipped}`
                }
              </span>
            )}
          </div>
        </div>

        <div className="admin-search" style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <div style={{ flex: 1, position: "relative" }}>
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          <button
            onClick={() => setShowFilters((f) => !f)}
            style={{
              padding: "10px 16px",
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--border-subtle)",
              background: showFilters ? "var(--color-highlight)" : "var(--bg-card)",
              color: showFilters ? "#fff" : "var(--text-secondary)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "13px",
              fontWeight: 500,
              transition: "all 0.15s",
            }}
          >
            <FiFilter size={14} />
            Filters
            {(beltFilter !== "all" || violationFilter !== "all" || scoreFilter !== "all" || deptFilter !== "all") && (
              <span style={{
                background: "#ef4444",
                color: "#fff",
                borderRadius: "50%",
                width: "18px",
                height: "18px",
                fontSize: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
              }}>
                {[beltFilter, violationFilter, scoreFilter, deptFilter].filter((f) => f !== "all").length}
              </span>
            )}
          </button>
        </div>

        {showFilters && (
          <div style={{
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
            padding: "14px 18px",
            background: "var(--bg-card)",
            border: "1px solid var(--border-subtle)",
            borderRadius: "var(--radius-lg)",
            marginBottom: "16px",
            alignItems: "center",
          }}>
            {/* Belt filter */}
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <label style={{ fontSize: "11px", color: "var(--text-secondary)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Belt</label>
              <select
                value={beltFilter}
                onChange={(e) => setBeltFilter(e.target.value)}
                style={{
                  padding: "6px 10px",
                  borderRadius: "var(--radius-sm)",
                  border: "1px solid var(--border-subtle)",
                  background: "var(--bg-primary)",
                  color: "var(--text-primary)",
                  fontSize: "13px",
                  minWidth: "120px",
                }}
              >
                <option value="all">All Belts</option>
                <option value="black">Black Belt</option>
                <option value="green">Green Belt</option>
                <option value="yellow">Yellow Belt</option>
                <option value="white">White Belt</option>
              </select>
            </div>

            {/* Score filter */}
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <label style={{ fontSize: "11px", color: "var(--text-secondary)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Score</label>
              <select
                value={scoreFilter}
                onChange={(e) => setScoreFilter(e.target.value)}
                style={{
                  padding: "6px 10px",
                  borderRadius: "var(--radius-sm)",
                  border: "1px solid var(--border-subtle)",
                  background: "var(--bg-primary)",
                  color: "var(--text-primary)",
                  fontSize: "13px",
                  minWidth: "120px",
                }}
              >
                <option value="all">All Scores</option>
                <option value="90+">90%+</option>
                <option value="70-89">70% – 89%</option>
                <option value="50-69">50% – 69%</option>
                <option value="<50">Below 50%</option>
              </select>
            </div>

            {/* Violation filter */}
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <label style={{ fontSize: "11px", color: "var(--text-secondary)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Violations</label>
              <select
                value={violationFilter}
                onChange={(e) => setViolationFilter(e.target.value)}
                style={{
                  padding: "6px 10px",
                  borderRadius: "var(--radius-sm)",
                  border: "1px solid var(--border-subtle)",
                  background: "var(--bg-primary)",
                  color: "var(--text-primary)",
                  fontSize: "13px",
                  minWidth: "120px",
                }}
              >
                <option value="all">All</option>
                <option value="flagged">Flagged Only</option>
                <option value="major">Major Only</option>
                <option value="minor">Minor Only</option>
                <option value="clean">Clean Only</option>
              </select>
            </div>

            {/* Organization filter */}
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <label style={{ fontSize: "11px", color: "var(--text-secondary)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Organization</label>
              <select
                value={deptFilter}
                onChange={(e) => setDeptFilter(e.target.value)}
                style={{
                  padding: "6px 10px",
                  borderRadius: "var(--radius-sm)",
                  border: "1px solid var(--border-subtle)",
                  background: "var(--bg-primary)",
                  color: "var(--text-primary)",
                  fontSize: "13px",
                  minWidth: "140px",
                }}
              >
                <option value="all">All Organizations</option>
                {uniqueDepts.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>

            {/* Clear filters */}
            {(beltFilter !== "all" || violationFilter !== "all" || scoreFilter !== "all" || deptFilter !== "all") && (
              <button
                onClick={() => { setBeltFilter("all"); setViolationFilter("all"); setScoreFilter("all"); setDeptFilter("all"); }}
                style={{
                  padding: "6px 12px",
                  borderRadius: "var(--radius-sm)",
                  border: "1px solid var(--border-subtle)",
                  background: "transparent",
                  color: "#ef4444",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: 600,
                  alignSelf: "flex-end",
                  marginBottom: "1px",
                }}
              >
                Clear All
              </button>
            )}
          </div>
        )}

        {orgCategories.length > 0 && (
          <div style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border-subtle)",
            borderRadius: "var(--radius-lg)",
            padding: "16px 20px",
            marginBottom: "16px",
          }}>
            <div
              onClick={() => setShowCategoryPanel((v) => !v)}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <div>
                <div style={{ fontSize: "15px", fontWeight: 700, color: "var(--text-primary)" }}>
                  Category-wise Performance
                </div>
                <div style={{ fontSize: "12px", color: "var(--text-secondary)", marginTop: "2px" }}>
                  Organization-wide accuracy across each topic (e.g. Prompt Engineering, Google Workspace &amp; Gemini)
                </div>
              </div>
              <FiChevronDown
                size={18}
                style={{
                  transform: showCategoryPanel ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                  color: "var(--text-secondary)",
                }}
              />
            </div>
            {showCategoryPanel && (() => {
              const sortedCategories = [...orgCategories].sort((a, b) => b.score_percentage - a.score_percentage);
              const pieData = sortedCategories
                .filter((c) => (c.correct || 0) > 0)
                .map((c, idx) => ({
                  name: c.category,
                  value: c.correct || 0,
                  total: c.total || 0,
                  percentage: c.score_percentage || 0,
                  fill: CATEGORY_PIE_COLORS[idx % CATEGORY_PIE_COLORS.length],
                }));

              return (
                <div
                  style={{
                    marginTop: "14px",
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                    gap: "24px",
                    alignItems: "start",
                  }}
                >
                  <div style={{ display: "grid", gap: "10px" }}>
                    <div style={{ fontSize: "11px", color: "var(--text-secondary)", marginBottom: "-4px" }}>
                      Click a category to see every session that answered it.
                    </div>
                    {sortedCategories.map((c, idx) => {
                      const pct = c.score_percentage || 0;
                      const color = pct >= 70 ? "#22c55e" : pct >= 40 ? "#eab308" : "#ef4444";
                      const dotColor = CATEGORY_PIE_COLORS[
                        pieData.findIndex((p) => p.name === c.category)
                      ] || CATEGORY_PIE_COLORS[idx % CATEGORY_PIE_COLORS.length];
                      return (
                        <div
                          key={c.category}
                          onClick={() => openCategoryDrilldown(c.category)}
                          style={{
                            cursor: "pointer",
                            padding: "6px 8px",
                            borderRadius: "6px",
                            transition: "background 0.15s",
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "13px", marginBottom: "4px", gap: "8px" }}>
                            <span style={{ fontWeight: 600, color: "var(--text-primary)", display: "inline-flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
                              <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: dotColor, flexShrink: 0 }} />
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.category}</span>
                            </span>
                            <span style={{ color, fontWeight: 700, whiteSpace: "nowrap" }}>
                              {c.correct}/{c.total} · {pct.toFixed(1)}%
                              {c.sessions_count != null && (
                                <span style={{ color: "var(--text-secondary)", fontWeight: 400, marginLeft: "8px" }}>
                                  ({c.sessions_count} session{c.sessions_count === 1 ? "" : "s"})
                                </span>
                              )}
                            </span>
                          </div>
                          <div style={{ height: "6px", borderRadius: "3px", background: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${pct}%`, background: color, transition: "width 0.3s ease" }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minHeight: "280px" }}>
                    <div style={{ fontSize: "11px", color: "var(--text-secondary)", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 600 }}>
                      Correct Answers by Category
                    </div>
                    {pieData.length === 0 ? (
                      <div style={{ color: "var(--text-secondary)", fontSize: "13px", padding: "40px 0" }}>
                        No correct answers recorded yet.
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={95}
                            paddingAngle={2}
                            onClick={(d) => d?.name && openCategoryDrilldown(d.name)}
                          >
                            {pieData.map((entry, idx) => (
                              <Cell key={idx} fill={entry.fill} cursor="pointer" />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value, _name, props) => {
                              const p = props?.payload || {};
                              return [`${value}/${p.total} correct · ${(p.percentage || 0).toFixed(1)}%`, p.name];
                            }}
                            contentStyle={{
                              background: "var(--bg-card)",
                              border: "1px solid var(--border-subtle)",
                              borderRadius: "8px",
                              fontSize: "12px",
                            }}
                          />
                          <Legend
                            verticalAlign="bottom"
                            iconType="circle"
                            wrapperStyle={{ fontSize: "11px", paddingTop: "8px" }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th onClick={() => handleSort("name")}>
                  Name{sortConfig.key === "name" && (sortConfig.direction === "asc" ? " ▲" : " ▼")}
                </th>
                <th onClick={() => handleSort("email")}>
                  Email{sortConfig.key === "email" && (sortConfig.direction === "asc" ? " ▲" : " ▼")}
                </th>
                <th onClick={() => handleSort("assessment_name")}>
                  Assessment{sortConfig.key === "assessment_name" && (sortConfig.direction === "asc" ? " ▲" : " ▼")}
                </th>
                <th onClick={() => handleSort("score")}>
                  Score{sortConfig.key === "score" && (sortConfig.direction === "asc" ? " ▲" : " ▼")}
                </th>
                <th onClick={() => handleSort("score_percentage")}>
                  %{sortConfig.key === "score_percentage" && (sortConfig.direction === "asc" ? " ▲" : " ▼")}
                </th>
                <th onClick={() => handleSort("belt")}>
                  Belt{sortConfig.key === "belt" && (sortConfig.direction === "asc" ? " ▲" : " ▼")}
                </th>
                <th onClick={() => handleSort("max_complexity_reached")}>
                  Level{sortConfig.key === "max_complexity_reached" && (sortConfig.direction === "asc" ? " ▲" : " ▼")}
                </th>
                <th onClick={() => handleSort("completed_at")}>
                  Completed{sortConfig.key === "completed_at" && (sortConfig.direction === "asc" ? " ▲" : " ▼")}
                </th>
                <th onClick={() => handleSort("major_violations")}>
                  Violations{sortConfig.key === "major_violations" && (sortConfig.direction === "asc" ? " ▲" : " ▼")}
                </th>
                <th style={{ width: 44, minWidth: 44, textAlign: "center", padding: "10px 6px" }}>
                  <FiDownload title="Report" style={{ opacity: 0.7 }} />
                </th>
                <th style={{ width: 44, minWidth: 44, textAlign: "center", padding: "10px 6px" }}>
                  <FiCamera title="Proctor" style={{ opacity: 0.7 }} />
                </th>
                <th style={{ width: 44, minWidth: 44, textAlign: "center", padding: "10px 6px" }}>
                  <FiChevronDown title="Detail" style={{ opacity: 0.7 }} />
                </th>
                <th style={{ width: 44, minWidth: 44, textAlign: "center", padding: "10px 6px" }}>
                  <FiTrash2 title="Delete attempt" style={{ opacity: 0.7 }} />
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredResults.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((item, index) => {
                const globalIndex = (currentPage - 1) * PAGE_SIZE + index;
                const isExpanded = expandedRows.has(globalIndex);
                const tierColor = tierColors[item.tier] || "#6366f1";

                return (
                  <React.Fragment key={globalIndex}>
                    <motion.tr
                      className="admin-table-row"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.03 }}
                    >
                      <td style={{ maxWidth: "140px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.name}>
                        <strong>{item.name}</strong>
                        {item.attempt_number > 1 && (
                          <span style={{
                            fontSize: "0.65rem",
                            background: "var(--color-highlight-soft)",
                            color: "var(--color-highlight)",
                            padding: "1px 5px",
                            borderRadius: "8px",
                            marginLeft: "4px",
                          }}>
                            #{item.attempt_number}
                          </span>
                        )}
                      </td>
                      <td style={{ maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.email}>{item.email}</td>
                      <td
                        style={{
                          fontSize: "11px",
                          color: "var(--text-secondary)",
                          maxWidth: "140px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={item.assessment_name || "—"}
                      >
                        {item.assessment_name || "—"}
                      </td>
                      <td>
                        <span className="score-badge">
                          {item.score}/{item.total_questions}
                        </span>
                      </td>
                      <td>
                        <span className="percentage-badge">
                          {item.score_percentage}%
                        </span>
                      </td>
                      <td title={item.tier}>
                        {item.belt ? (() => {
                          const bc = beltColorMap[item.belt_color] || "#6366f1";
                          return (
                            <span
                              className="tier-badge"
                              style={{
                                background: bc + "25",
                                color: bc,
                              }}
                            >
                              {item.belt}
                            </span>
                          );
                        })() : (
                          <span
                            className="tier-badge"
                            style={{
                              background: tierColor + "25",
                              color: tierColor,
                            }}
                          >
                            {item.tier}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className="level-badge">
                          Level {item.max_complexity_reached}
                        </span>
                      </td>
                      <td>
                        {item.completed_at
                          ? new Date(item.completed_at).toLocaleDateString()
                          : "\u2014"}
                      </td>
                      <td>
                        {(item.major_violations || 0) === 0 && (item.minor_violations || 0) === 0 ? (
                          <span className="violation-flag violation-false">Clean</span>
                        ) : (
                          <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                            {(item.major_violations || 0) > 0 && (
                              <span style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "3px",
                                padding: "2px 8px",
                                borderRadius: "10px",
                                fontSize: "11px",
                                fontWeight: 700,
                                background: "rgba(239,68,68,0.15)",
                                color: "#ef4444",
                              }}>
                                {item.major_violations} Major
                              </span>
                            )}
                            {(item.minor_violations || 0) > 0 && (
                              <span style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "3px",
                                padding: "2px 8px",
                                borderRadius: "10px",
                                fontSize: "11px",
                                fontWeight: 700,
                                background: "rgba(245,158,11,0.15)",
                                color: "#f59e0b",
                              }}>
                                {item.minor_violations} Minor
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td style={{ textAlign: "center", padding: "10px 6px" }}>
                        <button
                          className="download-btn"
                          onClick={() => handleDownloadReport(item)}
                          disabled={downloading === item.session_id}
                          title="Download Report"
                        >
                          {downloading === item.session_id ? (
                            <span className="btn-spinner" />
                          ) : (
                            <FiDownload />
                          )}
                        </button>
                      </td>
                      <td style={{ textAlign: "center", padding: "10px 6px" }}>
                        <button
                          className="download-btn violations-btn"
                          onClick={() => handleViewViolations(item.session_id)}
                          disabled={loadingViolations === item.session_id}
                          title="View Proctoring Logs"
                        >
                          {loadingViolations === item.session_id ? (
                            <span className="btn-spinner" />
                          ) : (
                            <FiAlertTriangle />
                          )}
                        </button>
                      </td>
                      <td style={{ textAlign: "center", padding: "10px 6px" }}>
                        <button
                          className="expand-btn"
                          onClick={() => toggleExpand(globalIndex, item.session_id)}
                          title={isExpanded ? "Collapse" : "Expand"}
                        >
                          <FiChevronDown
                            style={{
                              transform: isExpanded
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                            }}
                          />
                        </button>
                      </td>
                      <td style={{ textAlign: "center", padding: "10px 6px" }}>
                        <button
                          className="download-btn"
                          onClick={() => handleDeleteSession(item)}
                          disabled={deletingId === item.session_id}
                          title="Delete this attempt"
                          style={{ color: "#ef4444" }}
                        >
                          {deletingId === item.session_id ? (
                            <span className="btn-spinner" />
                          ) : (
                            <FiTrash2 />
                          )}
                        </button>
                      </td>
                    </motion.tr>

                    {isExpanded && (
                      <motion.tr
                        className="admin-expanded-row"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                      >
                        <td colSpan="12">
                          <div className="expanded-content">
                            <h4>Detailed Results</h4>
                            <div className="expanded-grid">
                              <div className="expanded-item">
                                <span className="expanded-label">User ID</span>
                                <span className="expanded-value">
                                  {item.user_id}
                                </span>
                              </div>
                              <div className="expanded-item">
                                <span className="expanded-label">Session ID</span>
                                <span className="expanded-value">
                                  {item.session_id}
                                </span>
                              </div>
                              <div className="expanded-item">
                                <span className="expanded-label">
                                  Total Questions
                                </span>
                                <span className="expanded-value">
                                  {item.total_questions}
                                </span>
                              </div>
                              <div className="expanded-item">
                                <span className="expanded-label">
                                  Max Complexity
                                </span>
                                <span className="expanded-value">
                                  Level {item.max_complexity_reached}
                                </span>
                              </div>
                              <div className="expanded-item">
                                <span className="expanded-label">
                                  Completed At
                                </span>
                                <span className="expanded-value">
                                  {item.completed_at
                                    ? new Date(
                                        item.completed_at
                                      ).toLocaleString()
                                    : "\u2014"}
                                </span>
                              </div>
                            </div>

                            {/* Per-session category breakdown */}
                            <div style={{ marginTop: "18px" }}>
                              <h4 style={{ margin: "0 0 10px 0" }}>Category-wise Score</h4>
                              {(() => {
                                const cats = sessionCategories[item.session_id];
                                if (cats === "loading" || cats === undefined) {
                                  return <p style={{ fontSize: "13px", color: "var(--text-secondary)" }}>Loading…</p>;
                                }
                                if (cats === "error") {
                                  return <p style={{ fontSize: "13px", color: "#ef4444" }}>Could not load category data.</p>;
                                }
                                if (!cats.length) {
                                  return <p style={{ fontSize: "13px", color: "var(--text-secondary)" }}>No answered questions.</p>;
                                }
                                return (
                                  <div style={{ display: "grid", gap: "8px" }}>
                                    {[...cats].sort((a, b) => b.score_percentage - a.score_percentage).map((c) => {
                                      const pct = c.score_percentage || 0;
                                      const color = pct >= 70 ? "#22c55e" : pct >= 40 ? "#eab308" : "#ef4444";
                                      return (
                                        <div key={c.category}>
                                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", marginBottom: "3px" }}>
                                            <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>{c.category}</span>
                                            <span style={{ color, fontWeight: 700 }}>
                                              {c.correct}/{c.total} · {pct.toFixed(1)}%
                                            </span>
                                          </div>
                                          <div style={{ height: "5px", borderRadius: "3px", background: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
                                            <div style={{ height: "100%", width: `${pct}%`, background: color }} />
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              })()}
                            </div>
                            <div style={{
                              marginTop: "18px",
                              paddingTop: "14px",
                              borderTop: "1px dashed var(--border-subtle)",
                              display: "flex",
                              justifyContent: "flex-end",
                            }}>
                              <button
                                onClick={() => handleDeleteUserHistory(item)}
                                disabled={deletingUserId === item.user_id}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  padding: "6px 12px",
                                  borderRadius: "6px",
                                  border: "1px solid rgba(239,68,68,0.4)",
                                  background: "rgba(239,68,68,0.08)",
                                  color: "#ef4444",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  cursor: deletingUserId === item.user_id ? "not-allowed" : "pointer",
                                }}
                                title="Wipe all attempts + retake permissions so this user can take the assessment again"
                              >
                                <FiTrash2 />
                                {deletingUserId === item.user_id ? "Deleting…" : "Delete all attempts for this user"}
                              </button>
                            </div>
                          </div>
                        </td>
                      </motion.tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(filteredResults.length / PAGE_SIZE)}
            onPageChange={setCurrentPage}
            totalItems={filteredResults.length}
            pageSize={PAGE_SIZE}
          />
        </div>

        {filteredResults.length === 0 && (
          <div className="empty-state">
            <p>No results found matching your search.</p>
          </div>
        )}
      </motion.div>

      {/* Category Drilldown Modal */}
      {categoryDrilldown && (
        <div
          className="modal-overlay"
          onClick={() => setCategoryDrilldown(null)}
        >
          <motion.div
            className="modal-content"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "780px", width: "100%" }}
          >
            <div className="modal-header">
              <div>
                <h2 style={{ margin: 0 }}>{categoryDrilldown.category}</h2>
                <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "var(--text-secondary)" }}>
                  Every session that answered questions in this category
                </p>
              </div>
              <button className="modal-close" onClick={() => setCategoryDrilldown(null)}>
                <FiX />
              </button>
            </div>

            <div style={{ padding: "20px 24px", maxHeight: "60vh", overflowY: "auto" }}>
              {categoryDrilldown.loading && (
                <p style={{ color: "var(--text-secondary)" }}>Loading…</p>
              )}
              {categoryDrilldown.error && (
                <p style={{ color: "#ef4444" }}>{categoryDrilldown.error}</p>
              )}
              {!categoryDrilldown.loading && !categoryDrilldown.error && categoryDrilldown.sessions?.length === 0 && (
                <p style={{ color: "var(--text-secondary)" }}>No sessions have answered this category yet.</p>
              )}
              {!categoryDrilldown.loading && categoryDrilldown.sessions?.length > 0 && (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                      <th style={{ textAlign: "left", padding: "10px 8px", fontSize: "11px", textTransform: "uppercase", color: "var(--text-secondary)", fontWeight: 700, letterSpacing: "0.4px" }}>Name</th>
                      <th style={{ textAlign: "left", padding: "10px 8px", fontSize: "11px", textTransform: "uppercase", color: "var(--text-secondary)", fontWeight: 700, letterSpacing: "0.4px" }}>Organization</th>
                      <th style={{ textAlign: "center", padding: "10px 8px", fontSize: "11px", textTransform: "uppercase", color: "var(--text-secondary)", fontWeight: 700, letterSpacing: "0.4px" }}>Category Score</th>
                      <th style={{ textAlign: "center", padding: "10px 8px", fontSize: "11px", textTransform: "uppercase", color: "var(--text-secondary)", fontWeight: 700, letterSpacing: "0.4px" }}>Overall</th>
                      <th style={{ textAlign: "center", padding: "10px 8px", fontSize: "11px", textTransform: "uppercase", color: "var(--text-secondary)", fontWeight: 700, letterSpacing: "0.4px" }}>Attempt</th>
                      <th style={{ textAlign: "left", padding: "10px 8px", fontSize: "11px", textTransform: "uppercase", color: "var(--text-secondary)", fontWeight: 700, letterSpacing: "0.4px" }}>Completed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryDrilldown.sessions.map((s) => {
                      const pct = s.category_score_percentage || 0;
                      const color = pct >= 70 ? "#22c55e" : pct >= 40 ? "#eab308" : "#ef4444";
                      return (
                        <tr key={s.session_id} style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                          <td style={{ padding: "10px 8px" }}>
                            <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>{s.user_name || "—"}</div>
                            <div style={{ fontSize: "11px", color: "var(--text-secondary)" }}>{s.user_email}</div>
                          </td>
                          <td style={{ padding: "10px 8px", color: "var(--text-secondary)" }}>
                            {s.business_unit || "—"}
                          </td>
                          <td style={{ padding: "10px 8px", textAlign: "center" }}>
                            <span style={{
                              display: "inline-block",
                              padding: "3px 10px",
                              borderRadius: "10px",
                              background: color + "25",
                              color,
                              fontWeight: 700,
                            }}>
                              {s.correct}/{s.total} · {pct.toFixed(1)}%
                            </span>
                          </td>
                          <td style={{ padding: "10px 8px", textAlign: "center", color: "var(--text-primary)" }}>
                            {(s.overall_score_percentage ?? 0).toFixed(1)}%
                          </td>
                          <td style={{ padding: "10px 8px", textAlign: "center", color: "var(--text-secondary)" }}>
                            #{s.attempt_number}
                          </td>
                          <td style={{ padding: "10px 8px", color: "var(--text-secondary)" }}>
                            {s.completed_at ? new Date(s.completed_at).toLocaleString() : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Proctoring Modal — Gallery + Timeline */}
      {violationsModal && (() => {
        const { data, galleryUrls } = violationsModal;
        const snapshotLogs = data.logs.filter((l) => l.has_snapshot && galleryUrls[l.id]);
        const violationLogs = data.logs.filter((l) => l.event_type !== "snapshot");

        return (
          <div className="modal-overlay" onClick={closeViolationsModal}>
            <motion.div
              className="modal-content proctoring-modal"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="modal-header">
                <h2>Proctoring Report</h2>
                <button className="modal-close" onClick={closeViolationsModal}>
                  <FiX />
                </button>
              </div>

              {/* Meta */}
              <div className="modal-meta">
                <span><strong>{data.user_name}</strong> ({data.user_email})</span>
                <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                  {(data.major_violations || 0) > 0 && (
                    <span style={{
                      padding: "3px 10px",
                      borderRadius: "10px",
                      fontSize: "12px",
                      fontWeight: 700,
                      background: "rgba(239,68,68,0.15)",
                      color: "#ef4444",
                    }}>
                      {data.major_violations} Major
                    </span>
                  )}
                  {(data.minor_violations || 0) > 0 && (
                    <span style={{
                      padding: "3px 10px",
                      borderRadius: "10px",
                      fontSize: "12px",
                      fontWeight: 700,
                      background: "rgba(245,158,11,0.15)",
                      color: "#f59e0b",
                    }}>
                      {data.minor_violations} Minor
                    </span>
                  )}
                  <span className="violation-count" style={{ opacity: 0.6 }}>
                    {snapshotLogs.length} snapshot{snapshotLogs.length !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>

              {/* Tab Switcher */}
              <div className="proctoring-tabs">
                <button
                  className={`proctor-tab ${galleryTab === "gallery" ? "active" : ""}`}
                  onClick={() => setGalleryTab("gallery")}
                >
                  <FiCamera /> Gallery ({snapshotLogs.length})
                </button>
                <button
                  className={`proctor-tab ${galleryTab === "timeline" ? "active" : ""}`}
                  onClick={() => setGalleryTab("timeline")}
                >
                  <FiAlertTriangle /> Violations ({violationLogs.length})
                </button>
              </div>

              {/* Gallery View */}
              {galleryTab === "gallery" && (
                <div className="gallery-container">
                  {/* Auto-analysis progress */}
                  {snapshotLogs.length > 0 && galleryIndex === null && (analyzing || analysisProgress) && (
                    <div className="analysis-banner">
                      {analyzing && analysisProgress?.total ? (() => {
                        const pct = Math.round((analysisProgress.current / analysisProgress.total) * 100);
                        return (
                          <>
                            <div className="analysis-header">
                              <span className="btn-spinner" />
                              <strong>Analyzing Activity</strong>
                              <span className="analysis-pct">{pct}%</span>
                            </div>
                            <div className="analysis-bar-track">
                              <div className="analysis-bar-fill" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="analysis-detail">Processing snapshot {analysisProgress.current} of {analysisProgress.total}</span>
                          </>
                        );
                      })() : analysisProgress?.done ? (
                        <div className="analysis-done">
                          <strong>{analysisProgress.flagged > 0
                            ? `Analysis Complete — ${analysisProgress.flagged} violation${analysisProgress.flagged !== 1 ? "s" : ""} detected across ${analysisProgress.total} snapshots`
                            : `Analysis Complete — No violations found across ${analysisProgress.total} snapshots`
                          }</strong>
                        </div>
                      ) : analysisProgress?.error ? (
                        <div className="analysis-done analysis-error">
                          <strong>Analysis failed — please try reopening the report</strong>
                        </div>
                      ) : null}
                    </div>
                  )}

                  {snapshotLogs.length === 0 ? (
                    <p className="empty-violations">No snapshots recorded.</p>
                  ) : galleryIndex !== null ? (
                    /* Lightbox — full-size single image */
                    <div className="lightbox">
                      <div className="lightbox-nav">
                        <button
                          disabled={galleryIndex <= 0}
                          onClick={() => setGalleryIndex(galleryIndex - 1)}
                        >
                          &#8592; Prev
                        </button>
                        <span className="lightbox-counter">
                          {galleryIndex + 1} / {snapshotLogs.length}
                          <span className="lightbox-time">
                            {snapshotLogs[galleryIndex]?.timestamp
                              ? new Date(snapshotLogs[galleryIndex].timestamp).toLocaleTimeString()
                              : ""}
                          </span>
                        </span>
                        <button
                          disabled={galleryIndex >= snapshotLogs.length - 1}
                          onClick={() => setGalleryIndex(galleryIndex + 1)}
                        >
                          Next &#8594;
                        </button>
                      </div>
                      {flaggedIds.has(snapshotLogs[galleryIndex]?.id) && (
                        <div className="lightbox-flagged">VIOLATION DETECTED</div>
                      )}
                      <img
                        className={`lightbox-img ${flaggedIds.has(snapshotLogs[galleryIndex]?.id) ? "lightbox-img-flagged" : ""}`}
                        src={galleryUrls[snapshotLogs[galleryIndex]?.id]}
                        alt={`Snapshot ${galleryIndex + 1}`}
                      />
                      <button className="lightbox-back" onClick={() => setGalleryIndex(null)}>
                        Back to Grid
                      </button>
                    </div>
                  ) : (
                    /* Thumbnail Grid */
                    <div className="gallery-grid">
                      {snapshotLogs.map((log, idx) => (
                        <div
                          key={log.id}
                          className={`gallery-thumb ${flaggedIds.has(log.id) ? "gallery-thumb-flagged" : ""}`}
                          onClick={() => setGalleryIndex(idx)}
                        >
                          <img src={galleryUrls[log.id]} alt={`Snapshot ${idx + 1}`} />
                          <span className="thumb-time">
                            {log.timestamp ? new Date(log.timestamp).toLocaleTimeString() : ""}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Timeline View */}
              {galleryTab === "timeline" && (
                <div className="violations-list">
                  {violationLogs.length === 0 ? (
                    <p className="empty-violations">No violations recorded for this session.</p>
                  ) : (
                    violationLogs.map((log) => {
                      const evtInfo = eventTypeLabels[log.event_type] || { label: log.event_type, color: "#94a3b8", severity: null };
                      const severity = log.severity || evtInfo.severity;
                      const severityColor = severity === "major" ? "#ef4444" : severity === "minor" ? "#f59e0b" : "#94a3b8";
                      return (
                        <div key={log.id} className="violation-item" style={{
                          borderLeft: `3px solid ${severityColor}`,
                          paddingLeft: "12px",
                        }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                            {severity && (
                              <span style={{
                                padding: "1px 6px",
                                borderRadius: "6px",
                                fontSize: "9px",
                                fontWeight: 800,
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                background: severityColor + "20",
                                color: severityColor,
                              }}>
                                {severity}
                              </span>
                            )}
                            <span
                              className="violation-type-badge"
                              style={{ background: evtInfo.color + "20", color: evtInfo.color }}
                            >
                              {evtInfo.label}
                            </span>
                          </div>
                          <span className="violation-desc">{log.description || "—"}</span>
                          <span className="violation-time">
                            {log.timestamp ? new Date(log.timestamp).toLocaleTimeString() : "—"}
                          </span>
                          {log.has_snapshot && galleryUrls[log.id] && (
                            <img
                              className="violation-thumb"
                              src={galleryUrls[log.id]}
                              alt="snapshot"
                              onClick={() => {
                                const idx = snapshotLogs.findIndex((s) => s.id === log.id);
                                if (idx >= 0) {
                                  setGalleryTab("gallery");
                                  setGalleryIndex(idx);
                                }
                              }}
                            />
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </motion.div>
          </div>
        );
      })()}
    </div>
  );
}
